import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
} from 'react';
import { useMsal } from '@azure/msal-react';
import { AccountInfo } from '@azure/msal-browser';
import toast from 'react-hot-toast';

import { authService, User } from '@/application/services/AuthService';
import { UsersService } from '@/services/Users';
import { createLogger } from '@/config/logger';
import { loginRequest, ADMIN_DOMAIN } from '@/config/authConfig';

const logger = createLogger('AuthProvider');

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
}

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: User }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'CLEAR_ERROR' };

const initialState: AuthState = {
  user: null,
  isLoading: true, // Começa carregando enquanto verifica MSAL
  isAuthenticated: false,
  error: null,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isLoading: false,
        isAuthenticated: true,
        error: null,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: false,
        error: action.payload,
      };
    case 'AUTH_LOGOUT':
      return {
        ...state,
        user: null,
        isLoading: false,
        isAuthenticated: false,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

interface AuthContextType extends AuthState {
  login: (email?: string) => Promise<void>;
  validateCode: (email: string, code: string) => Promise<void>; // Mantido por compatibilidade
  logout: () => Promise<void>;
  clearError: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

// Função auxiliar para criar objeto User básico a partir da conta Microsoft
const mapAdAccountToUser = (account: AccountInfo): User => {
  const email = account.username;
  const name = account.name || email.split('@')[0];
  const isAdmin = email.endsWith(ADMIN_DOMAIN);

  // Derivar userTag do email (sanitizado)
  const userTag = email
    .split('@')[0]
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

  return {
    id: 0,
    email: email,
    name: name,
    org: isAdmin ? 'Pagar' : 'Cliente',
    userTag: userTag,
    userType: isAdmin ? 'Dashboard' : 'PersonalGold',
    isCorporate: isAdmin,
    hasApi: isAdmin,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
};

// Função para buscar perfil do usuário na API do pagar-openfinance
const fetchUserProfileFromApi = async (email: string): Promise<User | null> => {
  try {
    logger.info('Fetching user profile from API', { email });
    const apiUser = await UsersService.getByEmail(email);
    
    if (apiUser) {
      logger.info('User profile found in API', { userTag: apiUser.userTag });
      return {
        id: apiUser.id,
        email: apiUser.email,
        name: apiUser.name,
        org: apiUser.org,
        userTag: apiUser.userTag,
        taxId: apiUser.taxId,
        pixKey: apiUser.pixKey,
        phoneNumber: apiUser.phoneNumber,
        apiKey: apiUser.apiKey,
        color: apiUser.color,
        isCorporate: apiUser.isCorporate,
        hasApi: apiUser.hasApi,
        createdAt: apiUser.createdAt,
        updatedAt: apiUser.updatedAt,
        userType: apiUser.userType,
      };
    }
    return null;
  } catch (error) {
    logger.warn('User profile not found in API, using AD account data', { email });
    return null;
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const { instance, accounts, inProgress } = useMsal();

  // Login via Microsoft AD
  const login = useCallback(
    async (_email?: string): Promise<void> => {
      try {
        dispatch({ type: 'AUTH_START' });

        // Iniciar fluxo de popup
        const loginResponse = await instance.loginPopup(loginRequest);
        const adUser = mapAdAccountToUser(loginResponse.account);

        // Mostrar toast de autenticação bem sucedida
        toast.success(`Login realizado com sucesso!\n${adUser.email}`, {
          duration: 4000,
          icon: '✅',
        });

        // Tentar buscar perfil completo do usuário na API do pagar-openfinance
        const apiUser = await fetchUserProfileFromApi(adUser.email);
        
        // Usar dados da API se disponíveis, senão usar dados do AD
        const finalUser = apiUser ? {
          ...apiUser,
          // Manter alguns campos do AD que podem ser mais atualizados
          name: apiUser.name || adUser.name,
        } : adUser;

        // Persistir sessão localmente
        authService.storeAuth(finalUser, loginResponse.accessToken);

        dispatch({ type: 'AUTH_SUCCESS', payload: finalUser });
        logger.info('Microsoft AD Login successful', { 
          email: finalUser.email,
          userTag: finalUser.userTag,
          fromApi: !!apiUser 
        });

      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Login failed';
        
        // Mostrar toast de erro
        toast.error(`Erro no login: ${errorMessage}`, {
          duration: 5000,
          icon: '❌',
        });

        dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
        logger.error('Login failed', { error: errorMessage });
        throw error;
      }
    },
    [instance]
  );

  // Função legado - mantida para compatibilidade
  const validateCode = useCallback(
    async (_email: string, _code: string): Promise<void> => {
      logger.warn('validateCode called but deprecated in favor of AD Auth');
      return Promise.resolve();
    },
    []
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      // Logout do MSAL
      await instance.logoutPopup();

      authService.logout();
      dispatch({ type: 'AUTH_LOGOUT' });

      toast.success('Logout realizado com sucesso', {
        duration: 3000,
        icon: '👋',
      });

      logger.info('User logged out successfully');
    } catch (error) {
      logger.error('Logout error', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      // Forçar logout local mesmo se o MSAL falhar
      authService.logout();
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  }, [instance]);

  const clearError = useCallback((): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  // Sincronizar estado do MSAL com o estado da aplicação
  useEffect(() => {
    const checkAuthStatus = async () => {
      // Primeiro verificar se há dados salvos no localStorage
      const storedUser = authService.getCurrentUser();
      const storedToken = authService.getToken();
      
      if (storedUser && storedToken) {
        // Já temos dados salvos, usar eles
        if (!state.isAuthenticated) {
          dispatch({ type: 'AUTH_SUCCESS', payload: storedUser });
        }
        return;
      }

      // Se não temos dados salvos, verificar MSAL
      const activeAccount = instance.getActiveAccount();

      if (activeAccount) {
        const adUser = mapAdAccountToUser(activeAccount);

        if (!state.isAuthenticated || state.user?.email !== adUser.email) {
          // Tentar buscar perfil da API
          const apiUser = await fetchUserProfileFromApi(adUser.email);
          const finalUser = apiUser || adUser;
          
          // Obter token silenciosamente se possível
          try {
            const tokenResponse = await instance.acquireTokenSilent({
              ...loginRequest,
              account: activeAccount,
            });
            authService.storeAuth(finalUser, tokenResponse.accessToken);
          } catch {
            // Se não conseguir token, ainda autenticar com dados locais
          }
          
          dispatch({ type: 'AUTH_SUCCESS', payload: finalUser });
        }
      } else if (accounts.length > 0) {
        instance.setActiveAccount(accounts[0]);
        const adUser = mapAdAccountToUser(accounts[0]);
        const apiUser = await fetchUserProfileFromApi(adUser.email);
        const finalUser = apiUser || adUser;
        
        dispatch({ type: 'AUTH_SUCCESS', payload: finalUser });
      } else {
        // Nenhuma conta logada
        if (state.isAuthenticated && !state.isLoading) {
          dispatch({ type: 'AUTH_LOGOUT' });
        } else if (state.isLoading && inProgress === 'none') {
          dispatch({ type: 'AUTH_FAILURE', payload: '' });
        }
      }
    };

    if (inProgress === 'none') {
      checkAuthStatus();
    }
  }, [
    instance,
    accounts,
    inProgress,
    state.isAuthenticated,
    state.isLoading,
    state.user?.email,
  ]);

  const value: AuthContextType = {
    ...state,
    isLoading: state.isLoading || inProgress !== 'none',
    login,
    validateCode,
    logout,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
